import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Folder, AlertTriangle, User, Clock, Plus, MapPin, LayoutGrid, List, Maximize2, Shield, Calendar, CalendarDays, FilterX } from "lucide-react";
import { PraticaService, ScadenzaService, IncaricoService, UserService, ServizioFiduciarioService } from '../services/apiService';
import { Pratica, ScadenzaDto, Incarico, User as UserType } from '../types';
import { useToast } from "../components/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import { Modal } from '../components/Modal';
import SearchableSelect, { SearchableOption } from '../components/SearchableSelect';
import { ConflittiDisponibilitaModal, ConflittoDisponibilita } from '../components/ConflittiDisponibilitaModal';
import { computeDataFinePreset, DATA_FINE_PRESETS, nowDatetimeLocal, isGiornoSuccessivo } from '../utils/utils';
import { clienteColor } from '../utils/clienteColor';
import { FiltroMulti } from '../components/FiltroMulti';
import { useAvvisoCompresenze } from '../components/AvvisoCompresenze';
import { getIncaricoStatoBadge, getTurnoStatoBadge } from '../utils/incaricoUtils';

interface CalendarEvent {
    id: number | string;
    date: Date;
    endDate?: Date;
    title: string;
    subtitle?: string;
    type: 'PRATICA' | 'NOTA' | 'INCARICO' | 'SERVIZIO';
    colorClass: string;
    originalData: any;
    // Etichetta di stato sulle card di incarichi e turni (Da accettare, Accettato,
    // Rifiutato, Revocato, In corso, Completato), come in IncaricoCard / TurnoCard.
    statoLabel?: string;
    statoBadgeClass?: string;
    // True per gli stati terminali (revocato/rifiutato): la card viene attenuata.
    statoInattivo?: boolean;
    // Id del cliente: incarichi e turni dello stesso cliente condividono il colore del
    // bordo (vedi clienteColor). Assente per gli urgenti non ancora abbinati.
    clienteId?: number;
    // Nome del cliente e collaboratore assegnato, per i filtri (assenti sulle scadenze).
    clienteNome?: string;
    collaboratoreId?: number;
    collaboratoreNome?: string;
    // Colore scelto nell'anagrafica cliente (#rrggbb); assente = automatico.
    clienteColore?: string;
}


// Filtro tipologia: tutti, solo investigazioni (pratiche, incarichi e scadenze,
// che nascono dai mandati) o solo servizi (turni fiduciari).
type FiltroTipo = 'TUTTI' | 'INVESTIGAZIONI' | 'SERVIZI';

// Card mostrate in una cella della vista mese; le altre si aprono col dettaglio del giorno.
const perNome = (a: string, b: string) => a.localeCompare(b, 'it', { sensitivity: 'base', numeric: true });

const MAX_CARD_MESE = 5;

// Lavoro a cui appartiene un evento, nel formato del filtro: turno → 'S:<servizio>',
// incarico o scadenza di un mandato → 'M:<mandato>', altrimenti ''.
const chiaveLavoro = (e: CalendarEvent): string => {
    if (e.type === 'SERVIZIO') return e.originalData?.servizioId != null ? `S:${e.originalData.servizioId}` : '';
    const praticaId = e.originalData?.pratica?.id ?? e.originalData?.praticaId;
    return praticaId != null ? `M:${praticaId}` : '';
};

export const CalendarioView: React.FC = () => {
    const navigate = useNavigate();
    const { addToast } = useToast();
    const { user, currentSedeId } = useAuth();

    // Stato calendario e vista
    const [currentDate, setCurrentDate] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const [events, setEvents] = useState<CalendarEvent[]>([]);

    // Vista corrente: mese (griglia), settimana (lun–dom), giorno o agenda.
    type CalView = 'mese' | 'settimana' | 'giorno' | 'agenda';
    const [viewMode, setViewMode] = useState<CalView>('mese');
    // Derivato per la logica esistente (sottotitolo, colori, legenda, agenda).
    const isVerticalView = viewMode === 'agenda';

    // Filtro tipologia eventi.
    const [filtroTipo, setFiltroTipo] = useState<FiltroTipo>('TUTTI');
    // Filtri cliente e collaboratore ('' = tutti), validi in tutte le viste.
    const [filtroClienti, setFiltroClienti] = useState<string[]>([]);
    const [filtroCollaboratori, setFiltroCollaboratori] = useState<string[]>([]);
    // Lavori aperti dei clienti scelti: 'M:<id mandato>' o 'S:<id servizio>'; vuoto = tutti.
    const [filtroLavori, setFiltroLavori] = useState<string[]>([]);

    // Modale dettaglio giorno (vista griglia)
    const [dayDetailModal, setDayDetailModal] = useState<{ date: Date, events: CalendarEvent[] } | null>(null);

    // Dati per la modale di assegnazione rapida
    const [praticheDisponibili, setPraticheDisponibili] = useState<Pratica[]>([]);
    const [collaboratoriDisponibili, setCollaboratoriDisponibili] = useState<UserType[]>([]);

    // Modale assegnazione
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignForm, setAssignForm] = useState({
        praticaId: '',
        soggettoNome: '',
        collaboratoreId: '',
        dataInizio: '',
        dataFine: '',
        luogo: '',
        note: '',
        // Scadenza opzionale entro cui il collaboratore deve accettare, come in DettaglioPraticaView.
        dataScadenzaAccettazione: '',
        // true se gli orari vengono dai tasti rapidi (Oggi/Domani/N giorni, fine 23:59):
        // in quel caso il backend non segnala orari difformi. Torna false appena
        // l'utente modifica a mano dataInizio o dataFine.
        orariPreset: false,
        continuativo: false,
    });
    const [isAssigning, setIsAssigning] = useState(false);
    // Avviso se sul servizio c'è già un altro collaboratore negli stessi orari (non blocca).
    const avvisoCompresenze = useAvvisoCompresenze();

    // Dal calendario si può pianificare un'investigazione (incarico su mandato) o un
    // turno di un servizio fiduciario. Il turno vale per il giorno cliccato: stesso
    // generatore di /servizi/{id} ma senza intervallo né giorni della settimana.
    const [assignMode, setAssignMode] = useState<'investigazione' | 'servizio'>('investigazione');
    const [serviziList, setServiziList] = useState<any[]>([]);
    const [turnoForm, setTurnoForm] = useState({
        servizioId: '', collaboratoreId: '', oraInizio: '08:00', oraFine: '14:00',
        notturno: false, dataScadenzaAccettazione: '',
        // Finestra di tolleranza opzionale per lato ("HH:mm" o '').
        rangeInizioDa: '', rangeInizioA: '', rangeFineDa: '', rangeFineA: '',
    });
    const [turnoTariffe, setTurnoTariffe] = useState<any[]>([]);
    const [turnoLoadingTariffe, setTurnoLoadingTariffe] = useState(false);

    // Modale conflitti e reinvio della stessa POST con forceOverride=true quando il
    // titolare conferma di assegnare comunque un collaboratore che ha fatto stop.
    const [conflittiAssegnazione, setConflittiAssegnazione] = useState<ConflittoDisponibilita[]>([]);
    const [conflittiSummary, setConflittiSummary] = useState<{ collab?: string; creati: number; totale: number }>({ creati: 0, totale: 0 });
    const [pendingAssignPayload, setPendingAssignPayload] = useState<any | null>(null);

    // Oggi a mezzanotte in formato YYYY-MM-DDTHH:MM: `min` degli input datetime-local
    // e controllo al submit per non pianificare nel passato.
    const todayMin = React.useMemo(() => {
        const d = new Date();
        d.setSeconds(0, 0);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T00:00`;
    }, []);

    // Opzioni dei select del modale Pianifica, in ordine alfabetico per nome.
    const opzioniMandati = React.useMemo<SearchableOption[]>(() => praticheDisponibili
        .map(p => {
            // Azienda: referente + ragione sociale (es. "Mario Rossi (Alfa Srl)"),
            // Privato: nome e cognome. Legge sia i campi di PraticaListDTO sia di PraticaDto.
            const tipo = (p as any).clienteTipo;
            const ragSoc = (p as any).clienteNome || (p as any).clientName || '—';
            const referente = (p as any).clienteReferente || (p as any).clientReferente;
            const cliente = (tipo === 'Azienda' && referente) ? `${referente} (${ragSoc})` : ragSoc;
            return { value: String(p.id), nome: p.name || '', label: `[${p.id}] ${p.name}`, sub: `Cliente: ${cliente}` };
        })
        .sort((a, b) => perNome(a.nome, b.nome)), [praticheDisponibili]);
    const opzioniAssegnatari = React.useMemo<SearchableOption[]>(() => collaboratoriDisponibili
        .map(c => ({ value: String(c.id), label: `${c.name || ''} ${c.surname || ''}`.trim() }))
        .sort((a, b) => perNome(a.label, b.label)), [collaboratoriDisponibili]);
    const opzioniServizi = React.useMemo<SearchableOption[]>(() => serviziList
        .map((s: any) => ({ value: String(s.id), nome: s.nome || '', label: s.nome || `Servizio #${s.id}`, sub: s.indirizzo || undefined }))
        .sort((a, b) => perNome(a.nome, b.nome)), [serviziList]);
    const opzioniTurnoCollaboratori = React.useMemo<SearchableOption[]>(() => turnoTariffe
        .filter((t: any) => t.collaboratore?.id != null)
        .map((t: any) => {
            const nome = `${t.collaboratore.name || ''} ${t.collaboratore.surname || ''}`.trim();
            return { value: String(t.collaboratore.id), nome, label: `${nome} (€${t.tariffaOraria}/h)` };
        })
        .sort((a, b) => perNome(a.nome, b.nome)), [turnoTariffe]);

    // Soggetti della pratica selezionata
    const soggettiDisponibili = React.useMemo(() => {
        if (!assignForm.praticaId) return [];
        const praticaSelezionata = praticheDisponibili.find(p => String(p.id) === assignForm.praticaId);
        if (!praticaSelezionata) return [];

        try {
            const dati = typeof (praticaSelezionata as any).datiAggiuntiviJson === 'string'
                ? JSON.parse((praticaSelezionata as any).datiAggiuntiviJson)
                : (praticaSelezionata as any).datiAggiuntiviJson || {};

            if (dati.soggetti && Array.isArray(dati.soggetti)) return dati.soggetti;

            // Fallback per i mandati vecchi
            if (dati.soggetto_nome || dati.soggetto_denominazione) {
                return [{ nome: dati.soggetto_nome || dati.soggetto_denominazione }];
            }
        } catch (e) { console.error("Errore parse soggetti calendario", e); }

        return [];
    }, [assignForm.praticaId, praticheDisponibili]);

    useEffect(() => {
        if (soggettiDisponibili.length === 1) {
            setAssignForm(prev => ({ ...prev, soggettoNome: soggettiDisponibili[0].nome }));
        }
    }, [soggettiDisponibili]);

    // Caricamento dati
    const loadData = async () => {
        setLoading(true);
        try {
            // Filtro per la sede selezionata ('ALL' = nessun filtro). Il BE filtra pratiche
            // e servizi con sedeId (il ListDTO non espone la sede). Gli incarichi vengono
            // poi ristretti ai mandati della sede.
            const sedeParam = currentSedeId === 'ALL' ? undefined : Number(currentSedeId);
            const [praticheResult, scadenzeResult, incarichiResult, usersResult, serviziResult] = await Promise.allSettled([
                // GET /praticas è paginato (default 10): al calendario servono tutte le pratiche
                // della sede, quindi passiamo una size esplicita.
                PraticaService.getAll({ size: 500, sedeId: sedeParam }),
                ScadenzaService.getAll(),
                IncaricoService.getAll(),
                UserService.getAll(),
                // Servizi fiduciari con i loro turni, filtrati per sede dal BE.
                ServizioFiduciarioService.getAll({ sedeId: sedeParam }),
            ]);

            const serviceNames = ['pratiche', 'scadenze', 'incarichi', 'users', 'servizi'];
            [praticheResult, scadenzeResult, incarichiResult, usersResult, serviziResult].forEach((r, i) => {
                if (r.status === 'rejected') console.error(`[Calendario] Errore caricamento ${serviceNames[i]}:`, r.reason);
            });

            const resolve = (r: PromiseSettledResult<any>) => r.status === 'fulfilled' ? r.value : null;
            const praticheRes = resolve(praticheResult);
            const scadenzeRes = resolve(scadenzeResult);
            const incarichiRes = resolve(incarichiResult);
            const usersRes = resolve(usersResult);
            const serviziRes = resolve(serviziResult);

            // extractList gestisce anche il doppio wrap `response.data.data.content`
            // (service che restituisce già response.data di un endpoint Response<Page>).
            const extractList = (res: any): any[] => {
                if (Array.isArray(res)) return res;
                if (Array.isArray(res?.data)) return res.data;
                if (Array.isArray(res?.data?.content)) return res.data.content;
                if (Array.isArray(res?.data?.data?.content)) return res.data.data.content;
                if (Array.isArray(res?.content)) return res.content;
                return [];
            };

            const listPratiche = extractList(praticheRes);
            const listScadenze = extractList(scadenzeRes);
            const listIncarichi = extractList(incarichiRes);
            const listUtenti = extractList(usersRes);
            const listServizi = extractList(serviziRes);
            // Servizi per il picker "Pianifica Turno".
            setServiziList(listServizi);

            if (listPratiche.length === 0 && praticheRes) {
                console.log('[Calendario] Pratiche vuote — shape grezza response:', praticheRes);
            }

            // Esclude le pratiche temporanee autogenerate (vedi useIncarichiData.ts)
            const isTempPraticaName = (name?: string) =>
                /^mandato\s+(temp|test)/i.test((name || '').trim());

            // Stati di Pratica: "Da Attivare", "Pianificato", "In Attesa di Firma", "Firmata",
            // "In Corso", "Saldata"/"Conclusa". Escludiamo solo quelli terminali, su cui non
            // ha senso assegnare un nuovo incarico.
            const STATI_PRATICA_TERMINALI = new Set([
                'SALDATA', 'CONCLUSA', 'CHIUSA', 'ANNULLATA', 'ARCHIVIATA',
            ]);
            const isPraticaAttiva = (p: any) => {
                const s = (p?.stato || '').toUpperCase().trim();
                return !STATI_PRATICA_TERMINALI.has(s);
            };
            // Diagnostica: nome e stato di ogni pratica e motivo dello scarto.
            console.log('[Calendario] Pratiche raw (nome/stato):',
                listPratiche.map((p: any) => ({
                    id: p.id, name: p.name || p.nome, stato: p.stato,
                    scartataPerStato: !isPraticaAttiva(p),
                    scartataPerTemp: isTempPraticaName(p.name || p.nome),
                }))
            );
            // Nel dropdown mostriamo tutti i mandati assegnabili, qualunque sia il nome.
            const praticheFiltrate = listPratiche.filter((p: Pratica) => isPraticaAttiva(p));
            setPraticheDisponibili(praticheFiltrate);
            // Anche un ADMIN può essere assegnatario (auto-assegnazione del titolare).
            const agentiFiltrati = listUtenti.filter((u: UserType) =>
                u.role === 'COLLABORATORE' || u.role === 'RESPONSABILE' || u.role === 'ADMIN'
            );
            setCollaboratoriDisponibili(agentiFiltrati);
            console.log('[Calendario] Dopo filtro:', {
                mandatiAttivi: praticheFiltrate.length,
                agenti: agentiFiltrati.length,
                statiPratichePresenti: Array.from(new Set(listPratiche.map((p: any) => p?.stato))),
                ruoliUtentiPresenti: Array.from(new Set(listUtenti.map((u: any) => u?.role))),
            });

            const mappedEvents: CalendarEvent[] = [];

            // Mandati della sede selezionata (pratiche già filtrate dal BE): /incarichi non
            // ha filtro sede, quindi restringiamo gli incarichi tramite il mandato.
            // Con 'ALL' il set contiene tutti i mandati.
            const sedePraticaIds = new Set<number>(listPratiche.map((p: any) => p.id));
            const clienteIdByPratica = new Map<number, number>(
                listPratiche.filter((p: any) => p?.clienteId != null).map((p: any) => [Number(p.id), Number(p.clienteId)]));
            const clienteColoreByPratica = new Map<number, string>(
                listPratiche.filter((p: any) => p?.clienteColore).map((p: any) => [Number(p.id), String(p.clienteColore)]));
            const clienteNomeByPratica = new Map<number, string>(
                listPratiche.filter((p: any) => p?.clienteNome || p?.clientName)
                    .map((p: any) => [Number(p.id), String(p.clienteNome || p.clientName)]));

            // Le pratiche non compaiono come eventi: il calendario mostra incarichi, turni
            // e scadenze. `listPratiche` serve al dropdown e al filtro sede.

            listScadenze.forEach((s: ScadenzaDto) => {
                if (s.dataScadenza) {
                    const isCompleted = s.completed;
                    mappedEvents.push({
                        id: `scad_${s.id}`,
                        date: new Date(s.dataScadenza),
                        title: s.titolo,
                        type: 'NOTA',
                        colorClass: isCompleted
                            ? 'bg-gray-50 text-gray-400 border-gray-300 line-through opacity-70'
                            : (isVerticalView ? 'bg-rose-50 text-rose-800 border-rose-300 hover:bg-rose-100 shadow-sm' : 'bg-rose-50 text-rose-700 border-rose-400 hover:bg-rose-100'),
                        originalData: s
                    });
                }
            });

            // Nessun filtro sul nome del mandato: escluderebbe incarichi legittimi.
            listIncarichi.forEach((inc: Incarico) => {
                // Con una sede selezionata restano solo gli incarichi dei suoi mandati. Gli
                // incarichi orfani (senza mandato) si vedono solo con "Tutte".
                if (sedeParam != null && !sedePraticaIds.has((inc.pratica as any)?.id)) return;
                // Gli AUTOGENERATO servono solo ad agganciare i file caricati dall'ufficio: nascono
                // già COMPLETATO e qui farebbero sembrare concluso un mandato ancora in corso.
                if ((inc as any).tipoOrigine === 'AUTOGENERATO') return;
                if (inc.dataInizio) {
                    const dataInizio = new Date(inc.dataInizio);
                    const orarioInizio = dataInizio.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
                    let orarioFine = inc.dataFine ? new Date(inc.dataFine).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '';

                    const collabName = inc.collaboratore ? `${inc.collaboratore.name} ${inc.collaboratore.surname}` : 'Non Ass.';
                    const praticaName = inc.pratica ? (inc.pratica.name || "Pratica") : "Sconosciuta";
                    const statoBadge = getIncaricoStatoBadge(inc);
                    // Stati terminali (revocato/rifiutato): card attenuata.
                    const baseClass = isVerticalView
                        ? 'bg-indigo-50/80 text-indigo-900 border-indigo-400 hover:bg-indigo-100 shadow-sm'
                        : 'bg-indigo-50 text-indigo-800 border-indigo-400 hover:bg-indigo-100 shadow-sm';
                    const colorClass = statoBadge?.inactive
                        ? `${baseClass} opacity-60`
                        : baseClass;

                    mappedEvents.push({
                        id: `inc_${inc.id}`,
                        date: dataInizio,
                        endDate: inc.dataFine ? new Date(inc.dataFine) : undefined,
                        title: praticaName,
                        subtitle: `${orarioInizio}${orarioFine ? (isVerticalView ? ' - ' : '-')+orarioFine : ''} | ${collabName}`,
                        type: 'INCARICO',
                        colorClass,
                        clienteId: clienteIdByPratica.get(Number(inc.pratica?.id ?? (inc as any).praticaId))
                            ?? (inc.pratica as any)?.client?.id ?? (inc.pratica as any)?.clienteId,
                        clienteColore: clienteColoreByPratica.get(Number(inc.pratica?.id ?? (inc as any).praticaId)),
                        clienteNome: clienteNomeByPratica.get(Number(inc.pratica?.id ?? (inc as any).praticaId)),
                        collaboratoreId: inc.collaboratore?.id,
                        collaboratoreNome: inc.collaboratore ? collabName : undefined,
                        originalData: inc,
                        statoLabel: statoBadge?.label,
                        statoBadgeClass: statoBadge?.cls,
                        statoInattivo: statoBadge?.inactive,
                    });
                }
            });

            // Turni dei servizi fiduciari (uno per data e collaboratore): la Date si
            // costruisce da dataTurno + oraInizio/oraFine. I turni senza dataTurno si ignorano.
            listServizi.forEach((s: any) => {
                const turni = Array.isArray(s.turni) ? s.turni : [];
                turni.forEach((t: any) => {
                    if (!t.dataTurno) return;
                    const oraInizio = t.oraInizio || '00:00';
                    const oraFine = t.oraFine || '23:59';
                    // Stringa ISO senza fuso: viene interpretata in ora locale.
                    const dataInizio = new Date(`${t.dataTurno}T${oraInizio}`);
                    // Turno notturno: oraFine è del giorno dopo. Anche i record più vecchi senza
                    // flag ma con oraFine < oraInizio sono trattati come notturni.
                    const isNotturno = t.notturno === true || (oraFine < oraInizio);
                    const dataFineRaw = new Date(`${t.dataTurno}T${oraFine}`);
                    const dataFine = isNotturno
                        ? new Date(dataFineRaw.getTime() + 24 * 60 * 60 * 1000)
                        : dataFineRaw;
                    if (isNaN(dataInizio.getTime())) return;

                    const collabName = t.collaboratore
                        ? `${t.collaboratore.name || ''} ${t.collaboratore.surname || ''}`.trim()
                        : 'Non Ass.';
                    const servizioName = s.nome || 'Servizio';

                    const statoBadgeServ = getTurnoStatoBadge(t);
                    const baseClassServ = isVerticalView
                        ? 'bg-emerald-50/80 text-emerald-900 border-emerald-400 hover:bg-emerald-100 shadow-sm'
                        : 'bg-emerald-50 text-emerald-800 border-emerald-400 hover:bg-emerald-100 shadow-sm';
                    const colorClassServ = statoBadgeServ?.inactive
                        ? `${baseClassServ} opacity-60`
                        : baseClassServ;

                    mappedEvents.push({
                        id: `serv_${s.id}_${t.id}`,
                        date: dataInizio,
                        endDate: isNaN(dataFine.getTime()) ? undefined : dataFine,
                        title: servizioName,
                        subtitle: `${oraInizio}${oraFine ? (isVerticalView ? ' - ' : '-') + oraFine : ''} | ${collabName}`,
                        type: 'SERVIZIO',
                        colorClass: colorClassServ,
                        clienteId: s.clientId ?? s.client?.id,
                        clienteColore: s.clienteColore,
                        clienteNome: s.clientName || undefined,
                        collaboratoreId: t.collaboratore?.id,
                        collaboratoreNome: t.collaboratore ? collabName : undefined,
                        originalData: {
                            ...t,
                            servizioId: s.id,
                            servizioNome: s.nome,
                            indirizzo: s.indirizzo,
                            luogo: s.indirizzo,
                        },
                        statoLabel: statoBadgeServ?.label,
                        statoBadgeClass: statoBadgeServ?.cls,
                        statoInattivo: statoBadgeServ?.inactive,
                    });
                });
            });

            // Ordine per data e ora
            mappedEvents.sort((a, b) => a.date.getTime() - b.date.getTime());
            setEvents(mappedEvents);
        } catch (error) {
            console.error("[Calendario] Errore imprevisto:", error);
            addToast("Errore caricamento calendario", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // Ricarica anche al cambio sede.
    }, [currentDate, isVerticalView, currentSedeId]); // Ricarica i colori in base alla vista

    // Filtro Tutti / Investigazioni / Servizi. Le scadenze (NOTA) appartengono
    // al gruppo investigazioni perché nascono dai mandati.
    // Con un filtro cliente o collaboratore attivo le scadenze, che non hanno né l'uno
    // né l'altro, non compaiono.
    const eventiPerTipo = React.useMemo(() => events.filter(e => {
        if (filtroTipo === 'TUTTI') return true;
        if (filtroTipo === 'SERVIZI') return e.type === 'SERVIZIO';
        // Investigazioni: tutto tranne i turni.
        return e.type !== 'SERVIZIO';
    }), [events, filtroTipo]);
    const visibleEvents = React.useMemo(() => eventiPerTipo.filter(e => {
        if (filtroClienti.length > 0 && !filtroClienti.includes(e.clienteNome ?? '')) return false;
        if (filtroCollaboratori.length > 0 && !filtroCollaboratori.includes(String(e.collaboratoreId ?? ''))) return false;
        // Con dei lavori scelti restano solo i turni dei servizi e gli incarichi dei mandati scelti.
        if (filtroLavori.length > 0 && !filtroLavori.includes(chiaveLavoro(e))) return false;
        return true;
    }), [eventiPerTipo, filtroClienti, filtroCollaboratori, filtroLavori]);

    // Opzioni dei filtri dagli eventi della tipologia scelta, in ordine alfabetico. Le voci
    // già selezionate restano in elenco anche se non hanno eventi, per poterle togliere.
    const opzioniClienti = React.useMemo(() =>
        Array.from(new Set([...eventiPerTipo.map(e => e.clienteNome).filter((n): n is string => !!n), ...filtroClienti]))
            .sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }))
            .map((n): [string, string] => [n, n]),
    [eventiPerTipo, filtroClienti]);
    // Lavori aperti dei clienti scelti: mandati non conclusi e servizi fiduciari non terminati,
    // secondo la tipologia. Con più clienti scelti il nome del cliente distingue le voci omonime.
    const opzioniFiltroLavori = React.useMemo(() => {
        if (filtroClienti.length === 0) return [] as [string, string][];
        const piuClienti = filtroClienti.length > 1;
        const out: [string, string][] = [];
        if (filtroTipo !== 'SERVIZI') {
            praticheDisponibili.forEach((p: any) => {
                const cliente = p.clienteNome || p.clientName || '';
                if (!filtroClienti.includes(cliente)) return;
                out.push([`M:${p.id}`, `Mandato [${p.id}] ${p.name || ''}`.trim() + (piuClienti ? ` — ${cliente}` : '')]);
            });
        }
        if (filtroTipo !== 'INVESTIGAZIONI') {
            serviziList.forEach((sv: any) => {
                const cliente = sv.clientName || '';
                if (!filtroClienti.includes(cliente) || sv.deleted || String(sv.stato || '').toLowerCase() === 'terminato') return;
                out.push([`S:${sv.id}`, `Servizio ${sv.nome || `#${sv.id}`}` + (piuClienti ? ` — ${cliente}` : '')]);
            });
        }
        return out.sort((x, y) => x[1].localeCompare(y[1], 'it', { sensitivity: 'base' }));
    }, [praticheDisponibili, serviziList, filtroClienti, filtroTipo]);
    // I lavori scelti seguono i clienti scelti e la tipologia.
    useEffect(() => {
        setFiltroLavori(prev => {
            const validi = prev.filter(id => opzioniFiltroLavori.some(([v]) => v === id));
            return validi.length === prev.length ? prev : validi;
        });
    }, [opzioniFiltroLavori]);
    const opzioniCollaboratori = React.useMemo(() => {
        const nomi = new Map<string, string>();
        events.forEach(e => { if (e.collaboratoreId != null && e.collaboratoreNome) nomi.set(String(e.collaboratoreId), e.collaboratoreNome); });
        const ids = new Set<string>(filtroCollaboratori);
        eventiPerTipo.forEach(e => { if (e.collaboratoreId != null && e.collaboratoreNome) ids.add(String(e.collaboratoreId)); });
        return Array.from(ids).map((id): [string, string] => [id, nomi.get(id) ?? 'Collaboratore'])
            .sort((a, b) => a[1].localeCompare(b[1], 'it', { sensitivity: 'base' }));
    }, [events, eventiPerTipo, filtroCollaboratori]);


    // True se la data è prima di oggi. Usato nel click e per nascondere il "+"
    // sui giorni passati.
    const isDateInPast = (d: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dd = new Date(d);
        dd.setHours(0, 0, 0, 0);
        return dd.getTime() < today.getTime();
    };

    // Modale assegnazione
    const handleOpenAssignModal = (date: Date, e?: React.MouseEvent) => {
        if(e) e.stopPropagation();

        // Niente pianificazioni nel passato. Se il click arriva comunque (es. tap su
        // mobile, dove l'hover non c'è) mostriamo un toast.
        if (isDateInPast(date)) {
            addToast('Non puoi pianificare un incarico in una data passata', 'info');
            return;
        }

        // YYYY-MM-DD con i componenti locali: toISOString() convertirebbe in UTC e a
        // mezzanotte locale darebbe il giorno precedente.
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        const dateString = `${y}-${m}-${d}`;
        setAssignForm({
            praticaId: '',
            collaboratoreId: '',
            soggettoNome: '',
            dataInizio: `${dateString}T09:00`,
            dataFine: `${dateString}T18:00`,
            luogo: '',
            note: '',
            dataScadenzaAccettazione: '',
            orariPreset: false,
            continuativo: false,
        });
        // Reset della parte "Servizio" (turno di un giorno).
        setAssignMode('investigazione');
        setTurnoForm({ servizioId: '', collaboratoreId: '', oraInizio: '08:00', oraFine: '14:00', notturno: false, dataScadenzaAccettazione: '', rangeInizioDa: '', rangeInizioA: '', rangeFineDa: '', rangeFineA: '' });
        setTurnoTariffe([]);
        if(dayDetailModal) setDayDetailModal(null);
        setShowAssignModal(true);
    };

    // Selezionato il servizio, carichiamo i collaboratori abilitati (tariffeCollaboratori),
    // gli unici assegnabili al turno.
    const onSelectServizioTurno = async (servizioId: string) => {
        setTurnoForm(f => ({ ...f, servizioId, collaboratoreId: '' }));
        setTurnoTariffe([]);
        if (!servizioId) return;
        setTurnoLoadingTariffe(true);
        try {
            const res: any = await ServizioFiduciarioService.getById(Number(servizioId));
            const data: any = res?.data ?? res ?? {};
            setTurnoTariffe(Array.isArray(data.tariffeCollaboratori) ? data.tariffeCollaboratori : []);
        } catch {
            addToast('Impossibile caricare i collaboratori del servizio', 'error');
        } finally {
            setTurnoLoadingTariffe(false);
        }
    };

    // Crea un turno per il giorno cliccato usando POST /servizi/{id}/turni/bulk con una sola riga.
    const submitTurnoCalendario = async () => {
        if (!turnoForm.servizioId || !turnoForm.collaboratoreId || !turnoForm.oraInizio || !turnoForm.oraFine) {
            return addToast('Compila Servizio, Operatore, Ora inizio e Ora fine', 'error');
        }
        const dataTurno = (assignForm.dataInizio || '').split('T')[0];
        if (!dataTurno) return addToast('Giorno del turno non valido', 'error');
        const scad = turnoForm.dataScadenzaAccettazione
            ? (turnoForm.dataScadenzaAccettazione.length === 16 ? `${turnoForm.dataScadenzaAccettazione}:00` : turnoForm.dataScadenzaAccettazione)
            : null;
        const row = {
            collaboratoreId: Number(turnoForm.collaboratoreId),
            dataTurno,
            oraInizio: turnoForm.oraInizio,
            oraFine: turnoForm.oraFine,
            notturno: turnoForm.notturno,
            rangeInizioDa: turnoForm.rangeInizioDa || null,
            rangeInizioA: turnoForm.rangeInizioA || null,
            rangeFineDa: turnoForm.rangeFineDa || null,
            rangeFineA: turnoForm.rangeFineA || null,
            dataScadenzaAccettazione: scad,
        };
        const procedi = await avvisoCompresenze.conferma(Number(turnoForm.servizioId), row.collaboratoreId,
            [{ dataTurno, oraInizio: row.oraInizio, oraFine: row.oraFine, notturno: !!row.notturno }]);
        if (!procedi) return;
        setIsAssigning(true);
        try {
            const res: any = await ServizioFiduciarioService.saveTurniBulk(Number(turnoForm.servizioId), [row]);
            const data: any = res?.data ?? res ?? {};
            const created: any[] = Array.isArray(data?.created) ? data.created : (Array.isArray(data) ? data : []);
            const conflicts: any[] = Array.isArray(data?.conflicts) ? data.conflicts : [];
            if (conflicts.length > 0 && created.length === 0) {
                addToast('Turno in conflitto: il collaboratore risulta già impegnato in quella fascia oraria.', 'info');
            } else {
                addToast(`Turno creato per il ${new Date(`${dataTurno}T00:00:00`).toLocaleDateString('it-IT')}.`, 'success');
                setShowAssignModal(false);
            }
            loadData();
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Errore durante la creazione del turno', 'error');
        } finally {
            setIsAssigning(false);
        }
    };

    const handleAssignSubmit = async () => {
        if (!assignForm.praticaId || !assignForm.collaboratoreId || !assignForm.dataInizio || !assignForm.dataFine) {
            return addToast("Compila Mandato, Collaboratore, Inizio e Fine", "error");
        }
        // Niente pianificazioni nel passato; confronto sui timestamp per evitare
        // problemi di formato con datetime-local.
        if (new Date(assignForm.dataInizio).getTime() < Date.now() - 60_000) {
            return addToast("Non puoi pianificare un incarico nel passato", "error");
        }
        // dataFine obbligatoria e successiva a dataInizio: il backend divide i periodi
        // di più giorni in tranche giornaliere.
        if (new Date(assignForm.dataFine).getTime() < new Date(assignForm.dataInizio).getTime()) {
            return addToast("La data di fine deve essere uguale o successiva alla data di inizio", "error");
        }

        setIsAssigning(true);
        try {
            await submitAssignCalendario(false);
        } catch (e) {
            addToast("Errore durante l'assegnazione dell'incarico", "error");
        } finally {
            setIsAssigning(false);
        }
    };

    // Checkbox visibile solo se la fine cade il giorno dopo l'inizio.
    const assignContinuativoEligible = isGiornoSuccessivo(assignForm.dataInizio, assignForm.dataFine);

    // POST /incarichi usata sia al primo invio sia al reinvio con forceOverride
    // dalla modale conflitti.
    const submitAssignCalendario = async (forceOverride: boolean) => {
        const payload = forceOverride && pendingAssignPayload
            ? { ...pendingAssignPayload, forceOverride: true }
            : {
                praticaId: Number(assignForm.praticaId),
                collaboratoreId: Number(assignForm.collaboratoreId),
                soggettoNome: assignForm.soggettoNome,
                dataInizio: assignForm.dataInizio,
                dataFine: assignForm.dataFine,
                luogo: assignForm.luogo,
                note: assignForm.note,
                dataScadenzaAccettazione: assignForm.dataScadenzaAccettazione || null,
                orariPreset: assignForm.orariPreset,
                continuativo: !!assignForm.continuativo && assignContinuativoEligible,
            };
        const res = await IncaricoService.create(payload);
        const data: any = (res as any)?.data || {};
        const created: any[] = Array.isArray(data?.created) ? data.created : (Array.isArray(data) ? data : []);
        const conflicts: ConflittoDisponibilita[] = Array.isArray(data?.conflicts) ? data.conflicts : [];

        if (conflicts.length > 0) {
            const collab: any = (collaboratoriDisponibili as any[]).find((u: any) => String(u.id) === String(payload.collaboratoreId));
            setConflittiSummary({
                collab: collab ? `${collab.name} ${collab.surname || ''}`.trim() : undefined,
                creati: created.length,
                totale: created.length + conflicts.length,
            });
            setConflittiAssegnazione(conflicts);
            // Come in DettaglioPraticaView: dopo un invio forzato non riproponiamo
            // l'override, altrimenti l'incarico appena creato diventerebbe un nuovo
            // conflitto e si andrebbe in loop.
            const someOverridable = !forceOverride && conflicts.some(c => (c as any).overridable);
            setPendingAssignPayload(someOverridable ? payload : null);
            addToast(res?.message || `Assegnati ${created.length} incarichi; ${conflicts.length} saltati per conflitto.`, 'info');
        } else {
            setPendingAssignPayload(null);
            addToast(res?.message || (forceOverride ? "Incarico assegnato con override" : "Incarico assegnato con successo!"), 'success');
        }
        if (!forceOverride) setShowAssignModal(false);
        loadData();
    };

    const handleConfermaOverrideCalendario = async () => {
        setConflittiAssegnazione([]);
        setIsAssigning(true);
        try { await submitAssignCalendario(true); }
        catch { addToast("Errore durante l'override", 'error'); }
        finally { setIsAssigning(false); }
    };

    // Click su un evento. Al collaboratore assegnato apre /incarichi sul record
    // (MieiIncarichiView legge openIncaricoId / openTurnoId dallo state).
    // Negli altri casi: mandato per gli incarichi, gestione servizio per i turni.
    const handleEventClick = (evt: CalendarEvent, e?: React.MouseEvent) => {
        if(e) e.stopPropagation();
        const myId = user?.id;
        if (evt.type === 'INCARICO') {
            const inc = evt.originalData;
            const isMine = myId != null && inc?.collaboratore?.id === myId;
            if (isMine) {
                navigate('/incarichi', {
                    state: {
                        openIncaricoId: inc.id,
                        openDate: inc.dataInizio,
                    },
                });
                return;
            }
            const pId = inc?.pratica?.id;
            if (pId) navigate(`/pratiche/${pId}`);
        } else if (evt.type === 'PRATICA') {
            const pId = evt.originalData?.id;
            if (pId) navigate(`/pratiche/${pId}`);
        } else if (evt.type === 'SERVIZIO') {
            const turno = evt.originalData;
            const isMine = myId != null && turno?.collaboratore?.id === myId;
            if (isMine) {
                navigate('/incarichi', { state: { openTurnoId: turno.id } });
                return;
            }
            // Il turno porta alla gestione del servizio per admin/responsabile.
            const sId = turno?.servizioId;
            if (sId) navigate(`/servizi/${sId}`);
        } else {
            addToast(`Scadenza: ${evt.title} \n ${evt.originalData.note || ''}`, 'info');
        }
    };

    // Navigazione date
    const getDaysInMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const getFirstDayOfMonth = (date: Date) => {
        const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
        return day === 0 ? 6 : day - 1;
    };

    const isSameDay = (d1: Date, d2: Date) => {
        return d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
    };

    // Un evento occupa un giorno se inizia quel giorno o se è multi-giorno e il
    // giorno è nel suo intervallo. Un evento notturno (una sola mezzanotte, es.
    // 23:00 → 07:00) compare solo nel giorno di partenza.
    const eventCoversDay = (e: CalendarEvent, cellDate: Date): boolean => {
        if (isSameDay(e.date, cellDate)) return true;
        if (!e.endDate) return false;
        const startDay = new Date(e.date.getFullYear(), e.date.getMonth(), e.date.getDate());
        const endDay = new Date(e.endDate.getFullYear(), e.endDate.getMonth(), e.endDate.getDate());
        const daysSpan = Math.round((endDay.getTime() - startDay.getTime()) / 86400000);
        const startMin = e.date.getHours() * 60 + e.date.getMinutes();
        const endMin = e.endDate.getHours() * 60 + e.endDate.getMinutes();
        const isOvernight = daysSpan === 1 && endMin <= startMin;
        if (isOvernight) return false; // Solo nel giorno di partenza (gestito sopra)
        return cellDate >= e.date && cellDate <= e.endDate;
    };

    // Bordo sinistro col colore del cliente, al posto di quello di default.
    const clienteStyle = (e: CalendarEvent): React.CSSProperties | undefined => {
        const c = clienteColor(e.clienteId, e.clienteColore);
        return c ? { borderLeftColor: c, borderLeftWidth: '6px' } : undefined;
    };

    // Navigazione per vista: 1 giorno, 7 giorni o 1 mese. "Oggi" torna alla data odierna.
    const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
    // Lunedì della settimana che contiene `d`.
    const startOfWeekMon = (d: Date) => {
        const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const dow = x.getDay(); // 0 = domenica … 6 = sabato
        return addDays(x, dow === 0 ? -6 : 1 - dow);
    };
    const goPrev = () => setCurrentDate(cd =>
        viewMode === 'giorno' ? addDays(cd, -1)
        : viewMode === 'settimana' ? addDays(cd, -7)
        : new Date(cd.getFullYear(), cd.getMonth() - 1, 1));
    const goNext = () => setCurrentDate(cd =>
        viewMode === 'giorno' ? addDays(cd, 1)
        : viewMode === 'settimana' ? addDays(cd, 7)
        : new Date(cd.getFullYear(), cd.getMonth() + 1, 1));
    const goToday = () => setCurrentDate(new Date());
    // Passando a giorno o settimana da un'altra vista si parte da oggi.
    const switchView = (v: CalView) => {
        if ((v === 'giorno' || v === 'settimana') && viewMode !== v) setCurrentDate(new Date());
        setViewMode(v);
    };

    // I 7 giorni (lun–dom) della settimana corrente.
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(startOfWeekMon(currentDate), i));

    const monthLabel = currentDate.toLocaleString('it-IT', { month: 'long', year: 'numeric' });
    const capitalizedMonth = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

    // Etichetta di navigazione per vista.
    const navLabel = (() => {
        if (viewMode === 'giorno') {
            const s = currentDate.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
            return s.charAt(0).toUpperCase() + s.slice(1);
        }
        if (viewMode === 'settimana') {
            const mon = weekDays[0], sun = weekDays[6];
            return mon.getMonth() === sun.getMonth()
                ? `${mon.getDate()}–${sun.getDate()} ${sun.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}`
                : `${mon.getDate()} ${mon.toLocaleDateString('it-IT', { month: 'short' })} – ${sun.getDate()} ${sun.toLocaleDateString('it-IT', { month: 'short', year: 'numeric' })}`;
        }
        return capitalizedMonth;
    })();

    // Eventi di un giorno, anche multi-giorno; i notturni solo nel giorno di partenza.
    const eventsOfDay = (cellDate: Date) => visibleEvents.filter(e => eventCoversDay(e, cellDate));

    const daysInMonth = getDaysInMonth(currentDate);

    // Dati vista griglia
    const startOffset = getFirstDayOfMonth(currentDate);
    const totalSlots = Math.ceil((daysInMonth + startOffset) / 7) * 7;

    // Dati vista agenda
    const daysArray = Array.from({ length: daysInMonth }, (_, i) => new Date(currentDate.getFullYear(), currentDate.getMonth(), i + 1));

    if (loading && events.length === 0) return <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-blue-600" size={32}/></div>;

    // Card evento compatta (griglia mese e settimana).
    const renderCompactEvent = (evt: CalendarEvent, idx: number) => (
        <div
            key={`${evt.id}-${idx}`}
            onClick={(e) => handleEventClick(evt, e)}
            className={`p-1.5 rounded-md border-l-[3px] text-[10px] sm:text-[11px] cursor-pointer transition-all hover:scale-[1.02] flex flex-col gap-0.5 ${evt.colorClass}`}
            style={clienteStyle(evt)}
            title={evt.title + (evt.subtitle ? `\n${evt.subtitle}` : '') + (evt.statoLabel ? `\nStato: ${evt.statoLabel}` : '')}
        >
            <div className="flex items-center gap-1 font-bold truncate">
                {evt.type === 'PRATICA' && <Folder size={10} className="shrink-0 opacity-70"/>}
                {evt.type === 'NOTA' && <AlertTriangle size={10} className="shrink-0 opacity-70"/>}
                {evt.type === 'INCARICO' && <User size={10} className="shrink-0 opacity-70"/>}
                {evt.type === 'SERVIZIO' && <Shield size={10} className="shrink-0 opacity-70"/>}
                <span className={`truncate ${evt.statoInattivo ? 'line-through' : ''}`}>{evt.title}</span>
            </div>
            {evt.statoLabel && (
                <span className={`self-start text-[8px] font-bold px-1 py-px rounded uppercase tracking-wide border ${evt.statoBadgeClass}`}>{evt.statoLabel}</span>
            )}
            {evt.subtitle && (
                <div className="text-[9px] font-medium opacity-80 flex items-center gap-1 truncate"><Clock size={8}/> {evt.subtitle}</div>
            )}
        </div>
    );

    // Card evento estesa (agenda e giorno).
    const renderAgendaEvent = (evt: CalendarEvent, idx: number) => (
        <div
            key={`${evt.id}-${idx}`}
            onClick={(e) => handleEventClick(evt, e)}
            className={`p-3 sm:p-4 rounded-xl border-l-4 cursor-pointer transition-all hover:scale-[1.01] hover:shadow-md flex flex-col sm:flex-row gap-2 sm:gap-6 justify-between items-start sm:items-center ${evt.colorClass}`}
            style={clienteStyle(evt)}
        >
            <div className="flex-1">
                <div className="flex items-center gap-2 font-bold text-sm sm:text-base mb-1.5 text-gray-900 flex-wrap">
                    {evt.type === 'PRATICA' && <Folder size={16} className="text-slate-500"/>}
                    {evt.type === 'NOTA' && <AlertTriangle size={16} className="text-rose-500"/>}
                    {evt.type === 'INCARICO' && <User size={16} className="text-indigo-600"/>}
                    {evt.type === 'SERVIZIO' && <Shield size={16} className="text-emerald-600"/>}
                    <span className={evt.statoInattivo ? 'line-through' : ''}>{evt.title}</span>
                    {evt.statoLabel && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider border ${evt.statoBadgeClass}`}>{evt.statoLabel}</span>
                    )}
                </div>
                {evt.subtitle && (
                    <div className="text-xs sm:text-sm font-semibold opacity-80 flex items-center gap-1.5"><Clock size={14}/> {evt.subtitle}</div>
                )}
            </div>
            <div className="w-full sm:w-auto sm:max-w-xs flex flex-col gap-1 text-xs sm:text-right mt-2 sm:mt-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-black/5">
                {(evt.type === 'INCARICO' || evt.type === 'SERVIZIO') && evt.originalData.luogo && (
                    <div className="flex items-center sm:justify-end gap-1 font-medium opacity-90"><MapPin size={12}/> {evt.originalData.luogo}</div>
                )}
                {(evt.type === 'NOTA' || evt.type === 'INCARICO') && evt.originalData.note && (
                    <div className="italic opacity-80 line-clamp-2">"{evt.originalData.note}"</div>
                )}
            </div>
        </div>
    );

    return (
        // Le viste non hanno scroll interni: i giorni restano espansi e scorre l'intera pagina.
        <div className={`p-6 flex flex-col min-h-[calc(100vh-80px)] bg-gray-50 pb-20 mx-auto w-full ${isVerticalView ? 'max-w-6xl' : ''}`}>

            {/* Intestazione */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 shrink-0 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <span className="bg-white p-2 rounded-lg border shadow-sm text-gray-700">🗓️</span>
                        Pianificazione Operativa
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        {viewMode === 'agenda' ? "Vista agenda dettagliata per controllare le attività."
                            : viewMode === 'giorno' ? "Le attività di un singolo giorno; usa ‹ › per scorrere giorno per giorno."
                            : viewMode === 'settimana' ? "La settimana (lun–dom); usa ‹ › per la settimana precedente/successiva."
                            : "Clicca su un giorno per espanderlo o sul + per assegnare un incarico."}
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
                    {/* Vista: Mese / Settimana / Giorno / Agenda */}
                    <div className="flex bg-gray-200/80 p-1 rounded-xl shadow-inner border border-gray-300 w-full sm:w-auto justify-center">
                        {([
                            { key: 'mese', label: 'Mese', icon: <LayoutGrid size={16}/> },
                            { key: 'settimana', label: 'Settimana', icon: <CalendarDays size={16}/> },
                            { key: 'giorno', label: 'Giorno', icon: <Calendar size={16}/> },
                            { key: 'agenda', label: 'Agenda', icon: <List size={16}/> },
                        ] as { key: CalView; label: string; icon: React.ReactNode }[]).map(v => (
                            <button
                                key={v.key}
                                onClick={() => switchView(v.key)}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg font-semibold text-sm transition-all ${viewMode === v.key ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:bg-gray-300/50'}`}
                            >
                                {v.icon} <span className="hidden sm:inline">{v.label}</span>
                            </button>
                        ))}
                    </div>

                    {/* Navigazione + Oggi */}
                    <div className="flex gap-2 bg-white p-1 rounded-xl border shadow-sm w-full sm:w-auto justify-between items-center">
                        <button onClick={goPrev} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors">
                            <ChevronLeft size={20}/>
                        </button>
                        <span className="px-3 py-2 font-bold text-gray-800 min-w-[180px] text-center capitalize flex items-center justify-center">
                            {navLabel}
                        </span>
                        <button onClick={goNext} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors">
                            <ChevronRight size={20}/>
                        </button>
                        <button onClick={goToday} className="ml-1 px-3 py-2 rounded-lg text-xs font-bold text-blue-600 hover:bg-blue-50 transition-colors" title="Vai a oggi">
                            Oggi
                        </button>
                    </div>
                </div>
            </div>

            {/* Filtro tipologia */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 mb-3">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider shrink-0">Mostra</span>
                <div className="flex shrink-0 bg-gray-200/80 p-1 rounded-xl shadow-inner border border-gray-300 w-full sm:w-auto">
                    <button
                        type="button"
                        onClick={() => setFiltroTipo('TUTTI')}
                        className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg font-semibold text-xs leading-tight sm:whitespace-nowrap transition-all ${filtroTipo === 'TUTTI' ? 'bg-white text-slate-800 shadow-sm' : 'text-gray-600 hover:bg-gray-300/50'}`}
                    >
                        Tutti
                    </button>
                    <button
                        type="button"
                        onClick={() => setFiltroTipo('INVESTIGAZIONI')}
                        className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg font-semibold text-xs leading-tight sm:whitespace-nowrap transition-all ${filtroTipo === 'INVESTIGAZIONI' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:bg-gray-300/50'}`}
                    >
                        Solo Servizi Investigativi
                    </button>
                    <button
                        type="button"
                        onClick={() => setFiltroTipo('SERVIZI')}
                        className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg font-semibold text-xs leading-tight sm:whitespace-nowrap transition-all ${filtroTipo === 'SERVIZI' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-600 hover:bg-gray-300/50'}`}
                    >
                        Solo Servizi Fiduciari
                    </button>
                </div>
                <FiltroMulti placeholder="Tutti i clienti" opzioni={opzioniClienti} selezionati={filtroClienti} onChange={setFiltroClienti}/>
                {opzioniFiltroLavori.length > 0 && (
                    <FiltroMulti placeholder="Tutti i lavori aperti" opzioni={opzioniFiltroLavori} selezionati={filtroLavori} onChange={setFiltroLavori}/>
                )}
                <FiltroMulti placeholder="Tutti i collaboratori" opzioni={opzioniCollaboratori} selezionati={filtroCollaboratori} onChange={setFiltroCollaboratori}/>
                {(filtroClienti.length > 0 || filtroCollaboratori.length > 0) && (
                    <button
                        type="button"
                        onClick={() => { setFiltroClienti([]); setFiltroCollaboratori([]); setFiltroLavori([]); }}
                        className="self-start sm:self-auto shrink-0 p-2 rounded-xl border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800"
                        title="Azzera filtri"
                        aria-label="Azzera filtri"
                    >
                        <FilterX size={16}/>
                    </button>
                )}
            </div>

            {/* Legenda */}
            <div className="flex flex-wrap gap-4 mb-4 text-xs font-semibold">
                <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${isVerticalView ? 'text-indigo-800 bg-indigo-50 border-indigo-200' : 'text-indigo-700 bg-indigo-50 border-indigo-200'}`}><User size={14}/> Incarichi</span>
                <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${isVerticalView ? 'text-emerald-800 bg-emerald-50 border-emerald-200' : 'text-emerald-700 bg-emerald-50 border-emerald-200'}`}><Shield size={14}/> Turni Fiduciari</span>
                <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${isVerticalView ? 'text-rose-800 bg-rose-50 border-rose-200' : 'text-rose-700 bg-rose-50 border-rose-200'}`}><AlertTriangle size={14}/> Scadenze / Note</span>
            </div>

            {/* Vista agenda */}
            {isVerticalView && (
                <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col overflow-hidden">
                    <div className="flex-1 divide-y divide-gray-100">
                        {daysArray.map((cellDate, i) => {
                            const isToday = isSameDay(new Date(), cellDate);
                            const isWeekend = cellDate.getDay() === 0 || cellDate.getDay() === 6;
                            const shortDayName = cellDate.toLocaleDateString('it-IT', { weekday: 'short' }).replace('.', '');

                            const dayEvents = visibleEvents.filter(e => eventCoversDay(e, cellDate));

                            const hasEvents = dayEvents.length > 0;

                            return (
                                <div key={i} className={`flex flex-col sm:flex-row group transition-colors ${isWeekend ? 'bg-slate-50/50' : 'bg-white'} hover:bg-slate-50`}>
                                    {/* Colonna sinistra: data */}
                                    <div className={`w-full sm:w-32 shrink-0 p-4 flex sm:flex-col items-center sm:items-end sm:justify-start gap-3 sm:gap-1 border-b sm:border-b-0 sm:border-r border-gray-100 ${isToday ? 'bg-blue-50/30' : ''}`}>
                                        <div className="flex items-baseline gap-2 sm:flex-col sm:items-end">
                                            <span className={`text-xs sm:text-sm font-bold uppercase tracking-wider ${isToday ? 'text-blue-600' : isWeekend ? 'text-rose-400' : 'text-slate-500'}`}>
                                                {shortDayName}
                                            </span>
                                            <span className={`text-2xl sm:text-3xl font-black w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full ${isToday ? 'bg-blue-600 text-white shadow-md' : 'text-slate-800'}`}>
                                                {cellDate.getDate()}
                                            </span>
                                        </div>
                                        {/* "+" nascosto per i giorni passati. */}
                                        {!isDateInPast(cellDate) && (
                                            <button
                                                onClick={(e) => handleOpenAssignModal(cellDate, e)}
                                                className="ml-auto sm:ml-0 sm:mt-2 opacity-100 sm:opacity-0 group-hover:opacity-100 bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white p-2 rounded-xl transition-all shadow-sm flex items-center gap-2 text-xs font-bold"
                                                title="Pianifica in questo giorno"
                                            >
                                                <Plus size={16} strokeWidth={3}/> <span className="sm:hidden">Aggiungi</span>
                                            </button>
                                        )}
                                    </div>

                                    {/* Colonna destra: eventi */}
                                    <div className="flex-1 p-3 sm:p-4 flex flex-col gap-3 min-h-[80px]">
                                        {!hasEvents ? (
                                            <div className="h-full flex items-center text-gray-400 text-sm italic px-2">
                                                Nessuna attività programmata.
                                            </div>
                                        ) : (
                                            dayEvents.map((evt, idx) => (
                                                <div
                                                    key={`${evt.id}-${idx}`}
                                                    onClick={(e) => handleEventClick(evt, e)}
                                                    className={`p-3 sm:p-4 rounded-xl border-l-4 cursor-pointer transition-all hover:scale-[1.01] hover:shadow-md flex flex-col sm:flex-row gap-2 sm:gap-6 justify-between items-start sm:items-center ${evt.colorClass}`}
                                                    style={clienteStyle(evt)}
                                                >
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 font-bold text-sm sm:text-base mb-1.5 text-gray-900 flex-wrap">
                                                            {evt.type === 'PRATICA' && <Folder size={16} className="text-slate-500"/>}
                                                            {evt.type === 'NOTA' && <AlertTriangle size={16} className="text-rose-500"/>}
                                                            {evt.type === 'INCARICO' && <User size={16} className="text-indigo-600"/>}
                                                            {evt.type === 'SERVIZIO' && <Shield size={16} className="text-emerald-600"/>}
                                                            <span className={evt.statoInattivo ? 'line-through' : ''}>{evt.title}</span>
                                                            {evt.statoLabel && (
                                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider border ${evt.statoBadgeClass}`}>
                                                                    {evt.statoLabel}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {evt.subtitle && (
                                                            <div className="text-xs sm:text-sm font-semibold opacity-80 flex items-center gap-1.5">
                                                                <Clock size={14}/> {evt.subtitle}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="w-full sm:w-auto sm:max-w-xs flex flex-col gap-1 text-xs sm:text-right mt-2 sm:mt-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-black/5">
                                                        {(evt.type === 'INCARICO' || evt.type === 'SERVIZIO') && evt.originalData.luogo && (
                                                            <div className="flex items-center sm:justify-end gap-1 font-medium opacity-90">
                                                                <MapPin size={12}/> {evt.originalData.luogo}
                                                            </div>
                                                        )}
                                                        {(evt.type === 'NOTA' || evt.type === 'INCARICO') && evt.originalData.note && (
                                                            <div className="italic opacity-80 line-clamp-2">
                                                                "{evt.originalData.note}"
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Vista griglia (mese) */}
            {viewMode === 'mese' && (
                <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col">
                    <div className="grid grid-cols-7 border-b border-gray-200 bg-slate-50 rounded-t-2xl">
                        {['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'].map(d => (
                            <div key={d} className="py-3 text-center text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-wider">
                                {d}
                            </div>
                        ))}
                    </div>
                    <div className="flex-1 grid grid-cols-7 divide-x divide-y divide-gray-100 bg-white rounded-b-2xl">
                        {Array.from({ length: totalSlots }).map((_, i) => {
                            const dayNumber = i - startOffset + 1;
                            const isValidDay = dayNumber > 0 && dayNumber <= daysInMonth;
                            const cellDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), dayNumber);
                            const isToday = isSameDay(new Date(), cellDate) && isValidDay;
                            const isWeekend = (i + 1) % 7 === 0 || (i + 1) % 7 === 6;

                            const dayEvents = isValidDay
                                ? visibleEvents.filter(e => eventCoversDay(e, cellDate))
                                : [];

                            return (
                                <div
                                    key={i}
                                    onClick={() => isValidDay && setDayDetailModal({ date: cellDate, events: dayEvents })}
                                    className={`p-1 sm:p-2 relative flex flex-col group transition-colors min-h-[140px] border-b border-r border-gray-100 ${!isValidDay ? 'bg-slate-50/50 text-transparent cursor-default' : 'bg-white hover:bg-slate-50 cursor-pointer'} ${isWeekend && isValidDay ? 'bg-slate-50/30' : ''}`}
                                >
                                    {isValidDay && (
                                        <>
                                            <div className="flex justify-between items-start mb-2 px-1">
                                                <span className={`text-sm font-bold w-7 h-7 flex items-center justify-center rounded-full ${isToday ? 'bg-blue-600 text-white shadow-md' : 'text-slate-700'}`}>
                                                    {dayNumber}
                                                </span>
                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                                    <button onClick={(e) => { e.stopPropagation(); setDayDetailModal({ date: cellDate, events: dayEvents }); }} className="bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-md p-1 transition-all shadow-sm" title="Espandi dettagli">
                                                        <Maximize2 size={16} />
                                                    </button>
                                                    {/* "+" nascosto per i giorni passati. */}
                                                    {!isDateInPast(cellDate) && (
                                                        <button onClick={(e) => handleOpenAssignModal(cellDate, e)} className="bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white rounded-md p-1 transition-all shadow-sm" title="Assegna Incarico">
                                                            <Plus size={16} strokeWidth={3}/>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex-1 space-y-1.5 px-0.5 pb-1">
                                                {dayEvents.slice(0, MAX_CARD_MESE).map((evt, idx) => (
                                                    <div
                                                        key={`${evt.id}-${idx}`}
                                                        onClick={(e) => handleEventClick(evt, e)}
                                                        className={`p-1.5 rounded-md border-l-[3px] text-[10px] sm:text-[11px] cursor-pointer transition-all hover:scale-[1.02] flex flex-col gap-0.5 ${evt.colorClass}`}
                                                        style={clienteStyle(evt)}
                                                        title={evt.title + (evt.subtitle ? `\n${evt.subtitle}` : '') + (evt.statoLabel ? `\nStato: ${evt.statoLabel}` : '')}
                                                    >
                                                        <div className="flex items-center gap-1 font-bold truncate">
                                                            {evt.type === 'PRATICA' && <Folder size={10} className="shrink-0 opacity-70"/>}
                                                            {evt.type === 'NOTA' && <AlertTriangle size={10} className="shrink-0 opacity-70"/>}
                                                            {evt.type === 'INCARICO' && <User size={10} className="shrink-0 opacity-70"/>}
                                                            {evt.type === 'SERVIZIO' && <Shield size={10} className="shrink-0 opacity-70"/>}
                                                            <span className={`truncate ${evt.statoInattivo ? 'line-through' : ''}`}>{evt.title}</span>
                                                        </div>
                                                        {evt.statoLabel && (
                                                            <span className={`self-start text-[8px] font-bold px-1 py-px rounded uppercase tracking-wide border ${evt.statoBadgeClass}`}>
                                                                {evt.statoLabel}
                                                            </span>
                                                        )}
                                                        {evt.subtitle && (
                                                            <div className="text-[9px] font-medium opacity-80 flex items-center gap-1 truncate">
                                                                <Clock size={8}/> {evt.subtitle}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                                {dayEvents.length > MAX_CARD_MESE && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); setDayDetailModal({ date: cellDate, events: dayEvents }); }}
                                                        className="w-full text-left text-[10px] sm:text-[11px] font-bold text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md px-1.5 py-1 transition-colors"
                                                    >
                                                        + altri {dayEvents.length - MAX_CARD_MESE}
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Vista settimana (lun–dom) */}
            {viewMode === 'settimana' && (
                <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col">
                    <div className="grid grid-cols-7 border-b border-gray-200 bg-slate-50 rounded-t-2xl">
                        {weekDays.map((d, i) => {
                            const isToday = isSameDay(new Date(), d);
                            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                            return (
                                <div key={i} className={`py-2 text-center ${isToday ? 'bg-blue-50' : ''}`}>
                                    <div className={`text-[10px] font-bold uppercase tracking-wider ${isToday ? 'text-blue-600' : isWeekend ? 'text-rose-400' : 'text-slate-500'}`}>
                                        {d.toLocaleDateString('it-IT', { weekday: 'short' }).replace('.', '')}
                                    </div>
                                    <div className={`mx-auto mt-0.5 text-base font-black w-8 h-8 flex items-center justify-center rounded-full ${isToday ? 'bg-blue-600 text-white shadow-md' : 'text-slate-700'}`}>
                                        {d.getDate()}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex-1 grid grid-cols-7 divide-x divide-gray-100 bg-white rounded-b-2xl">
                        {weekDays.map((cellDate, i) => {
                            const dayEvents = eventsOfDay(cellDate);
                            const isToday = isSameDay(new Date(), cellDate);
                            const isWeekend = cellDate.getDay() === 0 || cellDate.getDay() === 6;
                            return (
                                <div key={i} className={`p-1.5 flex flex-col group min-h-[400px] ${isToday ? 'bg-blue-50/30' : isWeekend ? 'bg-slate-50/40' : 'bg-white'} hover:bg-slate-50 transition-colors`}>
                                    <div className="flex justify-end mb-1 h-6">
                                        {/* "+" nascosto per i giorni passati. */}
                                        {!isDateInPast(cellDate) && (
                                            <button onClick={(e) => handleOpenAssignModal(cellDate, e)} className="opacity-0 group-hover:opacity-100 bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white rounded-md p-1 transition-all shadow-sm" title="Pianifica in questo giorno">
                                                <Plus size={14} strokeWidth={3}/>
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex-1 space-y-1.5">
                                        {dayEvents.length === 0 ? (
                                            <div className="text-center text-gray-300 text-[10px] italic pt-3">—</div>
                                        ) : dayEvents.map((evt, idx) => renderCompactEvent(evt, idx))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Vista giorno */}
            {viewMode === 'giorno' && (() => {
                const cellDate = currentDate;
                const dayEvents = eventsOfDay(cellDate);
                const isToday = isSameDay(new Date(), cellDate);
                return (
                    // Nessuno scroll interno: la giornata scorre con la pagina, senza una seconda barra.
                    <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col max-w-3xl w-full mx-auto">
                        <div className={`flex items-center justify-between px-5 py-4 border-b border-gray-200 rounded-t-2xl ${isToday ? 'bg-blue-50/40' : 'bg-slate-50'}`}>
                            <div className="flex items-center gap-3">
                                <span className={`text-2xl font-black w-12 h-12 flex items-center justify-center rounded-full ${isToday ? 'bg-blue-600 text-white shadow-md' : 'text-slate-800 bg-white border border-gray-200'}`}>
                                    {cellDate.getDate()}
                                </span>
                                <div>
                                    <div className="text-sm font-bold text-slate-800 capitalize">{cellDate.toLocaleDateString('it-IT', { weekday: 'long' })}</div>
                                    <div className="text-xs text-slate-500 capitalize">{cellDate.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                                </div>
                                {isToday && <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue-100 text-blue-700 uppercase tracking-wider">Oggi</span>}
                            </div>
                            {/* "+" nascosto per i giorni passati. */}
                            {!isDateInPast(cellDate) && (
                                <button onClick={(e) => handleOpenAssignModal(cellDate, e)} className="bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white px-3 py-2 rounded-xl transition-all shadow-sm flex items-center gap-2 text-xs font-bold">
                                    <Plus size={16} strokeWidth={3}/> Pianifica
                                </button>
                            )}
                        </div>
                        <div className="flex-1 p-4 flex flex-col gap-3">
                            {dayEvents.length === 0 ? (
                                <div className="text-center text-gray-400 py-12 italic">Nessuna attività programmata per questa giornata.</div>
                            ) : dayEvents.map((evt, idx) => renderAgendaEvent(evt, idx))}
                        </div>
                    </div>
                );
            })()}

            {/* Modale dettaglio giorno (vista griglia) */}
            {dayDetailModal && (
                <Modal
                    title={`Eventi del ${dayDetailModal.date.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}`}
                    onClose={() => setDayDetailModal(null)}
                >
                    <div className="flex flex-col gap-4">
                        {dayDetailModal.events.length === 0 ? (
                            <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                                Nessun evento pianificato per questa giornata.
                            </div>
                        ) : (
                            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                                {dayDetailModal.events.map((evt, idx) => (
                                    <div
                                        key={`detail-${evt.id}-${idx}`}
                                        onClick={() => {
                                            setDayDetailModal(null);
                                            handleEventClick(evt);
                                        }}
                                        className={`p-3 rounded-lg border-l-4 cursor-pointer hover:opacity-90 transition-opacity ${evt.colorClass}`}
                                        style={clienteStyle(evt)}
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 font-bold text-sm mb-1 flex-wrap">
                                                    {evt.type === 'PRATICA' && <Folder size={14} className="opacity-70"/>}
                                                    {evt.type === 'NOTA' && <AlertTriangle size={14} className="opacity-70"/>}
                                                    {evt.type === 'INCARICO' && <User size={14} className="opacity-70"/>}
                                                    {evt.type === 'SERVIZIO' && <Shield size={14} className="opacity-70"/>}
                                                    <span className={evt.statoInattivo ? 'line-through' : ''}>{evt.title}</span>
                                                    {evt.statoLabel && (
                                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider border ${evt.statoBadgeClass}`}>
                                                            {evt.statoLabel}
                                                        </span>
                                                    )}
                                                </div>
                                                {evt.subtitle && (
                                                    <div className="text-xs font-medium opacity-90 flex items-center gap-1.5 mb-1">
                                                        <Clock size={12}/> {evt.subtitle}
                                                    </div>
                                                )}
                                                {evt.type === 'NOTA' && evt.originalData.note && (
                                                    <div className="text-xs mt-2 opacity-80 italic border-t border-black/10 pt-1">
                                                        Note: {evt.originalData.note}
                                                    </div>
                                                )}
                                                {(evt.type === 'INCARICO' || evt.type === 'SERVIZIO') && evt.originalData.luogo && (
                                                    <div className="text-xs mt-1 opacity-90">
                                                        📍 Luogo: {evt.originalData.luogo}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {/* "Aggiungi" nascosto se il giorno è passato. */}
                        {!isDateInPast(dayDetailModal.date) && (
                            <button
                                onClick={() => handleOpenAssignModal(dayDetailModal.date)}
                                className="mt-2 w-full py-3 bg-white border-2 border-blue-100 text-blue-600 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors"
                            >
                                <Plus size={18} /> Aggiungi Nuovo Incarico
                            </button>
                        )}
                    </div>
                </Modal>
            )}

            {/* Modale assegnazione rapida */}
            {showAssignModal && (
                <Modal title={assignMode === 'servizio' ? 'Pianifica Turno Servizio' : 'Pianifica Incarico Operativo'} onClose={() => setShowAssignModal(false)}>
                    <div className="space-y-4">
                        <div className={`p-3 rounded-lg border text-xs font-medium flex items-center gap-2 mb-2 ${assignMode === 'servizio' ? 'bg-emerald-50 border-emerald-100 text-emerald-800' : 'bg-indigo-50 border-indigo-100 text-indigo-800'}`}>
                            <Clock size={16} className={assignMode === 'servizio' ? 'text-emerald-500' : 'text-indigo-500'}/>
                            Stai programmando per il giorno: {new Date(assignForm.dataInizio).toLocaleDateString('it-IT')}.
                        </div>

                        {/* Tipo: incarico (su mandato, indaco) o turno (su servizio, verde). */}
                        <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-xl">
                            <button
                                type="button"
                                onClick={() => setAssignMode('investigazione')}
                                className={`py-2.5 rounded-lg text-sm font-bold transition-colors ${assignMode === 'investigazione' ? 'bg-indigo-600 text-white shadow' : 'text-gray-600 hover:bg-gray-200'}`}
                            >
                                Incarico
                            </button>
                            <button
                                type="button"
                                onClick={() => setAssignMode('servizio')}
                                className={`py-2.5 rounded-lg text-sm font-bold transition-colors ${assignMode === 'servizio' ? 'bg-emerald-600 text-white shadow' : 'text-gray-600 hover:bg-gray-200'}`}
                            >
                                Turno
                            </button>
                        </div>

                        {assignMode === 'investigazione' && (<>
                        {/* Selettore mandato */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Seleziona Mandato *</label>
                            <SearchableSelect
                                className="border-gray-300"
                                value={String(assignForm.praticaId || '')}
                                onChange={v => {
                                    // Cambiando mandato si azzera il soggetto
                                    setAssignForm({...assignForm, praticaId: v, soggettoNome: ''});
                                }}
                                placeholder="-- Scegli il mandato attivo --"
                                searchPlaceholder="Cerca mandato o cliente…"
                                emptyText="Nessun mandato"
                                options={opzioniMandati}
                            />
                        </div>

                        {/* Selettore soggetto (solo se ci sono soggetti) */}
                        {soggettiDisponibili.length > 0 && (
                            <div>
                                <label className="block text-xs font-bold text-orange-700 uppercase tracking-wider mb-1">Soggetto Investigato</label>
                                {soggettiDisponibili.length === 1 ? (
                                    <div className="w-full p-3 border border-orange-200 rounded-xl bg-orange-50 text-gray-800 font-medium">
                                        {soggettiDisponibili[0].nome}
                                    </div>
                                ) : (
                                    <select
                                        className="w-full p-3 border border-orange-200 rounded-xl bg-orange-50 focus:ring-2 focus:ring-orange-400 font-medium outline-none"
                                        value={assignForm.soggettoNome}
                                        onChange={e => setAssignForm({...assignForm, soggettoNome: e.target.value})}
                                    >
                                        <option value="">Tutti / Non specificato</option>
                                        {soggettiDisponibili.map((sogg: any, idx: number) => (
                                            <option key={idx} value={sogg.nome}>{sogg.nome}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}

                        {/* Selettore collaboratore */}

                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Assegna a *</label>
                            <SearchableSelect
                                className="border-gray-300"
                                value={String(assignForm.collaboratoreId || '')}
                                onChange={v => setAssignForm({...assignForm, collaboratoreId: v})}
                                placeholder="-- Seleziona l'agente --"
                                searchPlaceholder="Cerca collaboratore…"
                                emptyText="Nessun collaboratore"
                                options={opzioniAssegnatari}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Inizio Previsto *</label>
                                <input type="datetime-local" className="w-full p-3 border border-gray-300 rounded-xl font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                                       value={assignForm.dataInizio}
                                       min={todayMin}
                                       onChange={e => setAssignForm({...assignForm, dataInizio: e.target.value, orariPreset: false})}/>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Fine Prevista *</label>
                                <input type="datetime-local" className="w-full p-3 border border-gray-300 rounded-xl font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                                       value={assignForm.dataFine}
                                       min={assignForm.dataInizio || todayMin}
                                       onChange={e => setAssignForm({...assignForm, dataFine: e.target.value, orariPreset: false})}/>
                            </div>
                        </div>

                        {/*
                          Durate rapide: se dataInizio è vuota la imposta a ora, poi porta dataFine
                          alle 23:59 del giorno corrispondente. Il backend divide il periodo in
                          incarichi giornalieri.
                        */}
                        <div className="flex flex-wrap gap-2 -mt-2">
                            {DATA_FINE_PRESETS.map(p => (
                                <button
                                    key={p.label}
                                    type="button"
                                    onClick={() => {
                                        const dataInizio = assignForm.dataInizio || nowDatetimeLocal();
                                        setAssignForm({
                                            ...assignForm,
                                            dataInizio,
                                            dataFine: computeDataFinePreset(dataInizio, p.daysOffset),
                                            orariPreset: true,
                                        });
                                    }}
                                    className="px-3 py-1.5 text-xs font-bold rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition-colors"
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        {/*
                          Solo se la fine cade il giorno dopo l'inizio: un unico incarico a cavallo
                          della mezzanotte invece di due tranche.
                        */}
                        {assignContinuativoEligible && (
                            <label className="flex items-start gap-2.5 p-3 rounded-lg border border-indigo-200 bg-indigo-50 cursor-pointer">
                                <input type="checkbox" className="mt-0.5" checked={!!assignForm.continuativo}
                                       onChange={e => setAssignForm({...assignForm, continuativo: e.target.checked})}/>
                                <span className="text-xs text-indigo-900">
                                    <span className="font-bold">Continuativo</span> — un solo incarico a cavallo della mezzanotte:
                                    un solo report (datato al giorno di inizio), Start e Stop possibili in entrambi i giorni,
                                    ore cliente conteggiate oltre la mezzanotte. Senza spunta vengono creati due incarichi giornalieri.
                                </span>
                            </label>
                        )}

                        {/*
                          Scadenza di accettazione opzionale, come in DettaglioPraticaView: allo scadere
                          lo scheduler rifiuta l'incarico non accettato.
                        */}
                        <div className="bg-amber-50/60 border border-amber-200 rounded-lg p-3">
                            <label className="block text-xs font-bold text-amber-800 uppercase tracking-wider mb-1.5">
                                Scadenza accettazione <span className="font-normal normal-case text-amber-700/70">(opzionale)</span>
                            </label>
                            <input
                                type="datetime-local"
                                className="w-full p-2.5 border border-amber-200 rounded-lg bg-white focus:ring-2 focus:ring-amber-300 outline-none"
                                value={assignForm.dataScadenzaAccettazione}
                                onChange={e => setAssignForm({ ...assignForm, dataScadenzaAccettazione: e.target.value })}
                                min={todayMin}
                                max={assignForm.dataInizio || undefined}
                            />
                            <p className="text-[11px] text-amber-700/80 mt-1.5 leading-snug">
                                Se compilata, l'incarico verrà <strong>rifiutato automaticamente</strong> qualora il collaboratore non abbia cliccato "Accetta" entro questa data/ora.
                            </p>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Direttive / Note</label>
                            <textarea className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm resize-none"
                                      rows={3} placeholder="Istruzioni specifiche per il collaboratore..." value={assignForm.note} onChange={e => setAssignForm({...assignForm, note: e.target.value})}/>
                        </div>

                        <div className="pt-2">
                            <button
                                onClick={handleAssignSubmit}
                                disabled={
                                    isAssigning ||
                                    !assignForm.praticaId ||
                                    !assignForm.collaboratoreId ||
                                    !assignForm.dataInizio ||
                                    !assignForm.dataFine
                                }
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3.5 rounded-xl font-bold shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isAssigning ? <Loader2 className="animate-spin" size={20}/> : <User size={20}/>}
                                {isAssigning ? 'Assegnazione in corso...' : 'Conferma Incarico e Avvisa'}
                            </button>
                        </div>
                        </>)}

                        {assignMode === 'servizio' && (<>
                            {/* Turno di un giorno: servizio → operatore abilitato → orari. */}
                            <div>
                                <label className="block text-xs font-bold text-emerald-800 uppercase tracking-wider mb-1">Servizio *</label>
                                <SearchableSelect
                                    className="border-emerald-200"
                                    value={String(turnoForm.servizioId || '')}
                                    onChange={v => onSelectServizioTurno(v)}
                                    placeholder="-- Scegli il servizio --"
                                    searchPlaceholder="Cerca servizio…"
                                    emptyText="Nessun servizio"
                                    options={opzioniServizi}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-emerald-800 uppercase tracking-wider mb-1">Assegna a (tra abilitati) *</label>
                                <SearchableSelect
                                    className="border-emerald-200"
                                    value={String(turnoForm.collaboratoreId || '')}
                                    onChange={v => setTurnoForm({ ...turnoForm, collaboratoreId: v })}
                                    disabled={!turnoForm.servizioId || turnoLoadingTariffe}
                                    placeholder={turnoLoadingTariffe ? 'Caricamento…' : '-- Seleziona tra abilitati --'}
                                    searchPlaceholder="Cerca collaboratore…"
                                    emptyText="Nessun collaboratore abilitato"
                                    options={opzioniTurnoCollaboratori}
                                />
                                {turnoForm.servizioId && !turnoLoadingTariffe && turnoTariffe.length === 0 && (
                                    <p className="text-[11px] text-amber-600 mt-1">Nessun collaboratore abilitato su questo servizio. Aggiungi le tariffe dalla pagina del servizio.</p>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Ora inizio *</label>
                                    <input type="time" className="w-full p-3 border border-gray-300 rounded-xl font-medium focus:ring-2 focus:ring-emerald-500 outline-none"
                                           value={turnoForm.oraInizio} onChange={e => setTurnoForm({ ...turnoForm, oraInizio: e.target.value })}/>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Ora fine *</label>
                                    <input type="time" className="w-full p-3 border border-gray-300 rounded-xl font-medium focus:ring-2 focus:ring-emerald-500 outline-none"
                                           value={turnoForm.oraFine} onChange={e => setTurnoForm({ ...turnoForm, oraFine: e.target.value })}/>
                                </div>
                            </div>

                            <label className="flex items-start gap-2.5 p-3 rounded-lg border border-emerald-200 bg-emerald-50 cursor-pointer">
                                <input type="checkbox" className="mt-0.5" checked={turnoForm.notturno}
                                       onChange={e => setTurnoForm({ ...turnoForm, notturno: e.target.checked })}/>
                                <span className="text-xs text-emerald-900">
                                    <span className="font-bold">Turno notturno</span> — termina il giorno successivo (la fine cade oltre la mezzanotte).
                                </span>
                            </label>

                            {/*
                              Finestra di tolleranza opzionale per lato: in approvazione, se la timbratura
                              cade nella finestra, si propone l'orario previsto.
                            */}
                            <div className="border border-emerald-200 rounded-lg p-3">
                                <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-1">Range di tolleranza <span className="font-normal normal-case text-emerald-700/70">(opzionale)</span></p>
                                <p className="text-[11px] text-emerald-700/80 mb-2">Se la timbratura cade nel range, in approvazione il valore proposto userà l'orario previsto del turno.</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Inizio da</label>
                                        <input type="time" className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                                               value={turnoForm.rangeInizioDa} onChange={e => setTurnoForm({ ...turnoForm, rangeInizioDa: e.target.value })}/>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Inizio a</label>
                                        <input type="time" className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                                               value={turnoForm.rangeInizioA} onChange={e => setTurnoForm({ ...turnoForm, rangeInizioA: e.target.value })}/>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Fine da</label>
                                        <input type="time" className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                                               value={turnoForm.rangeFineDa} onChange={e => setTurnoForm({ ...turnoForm, rangeFineDa: e.target.value })}/>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Fine a</label>
                                        <input type="time" className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                                               value={turnoForm.rangeFineA} onChange={e => setTurnoForm({ ...turnoForm, rangeFineA: e.target.value })}/>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-amber-50/60 border border-amber-200 rounded-lg p-3">
                                <label className="block text-xs font-bold text-amber-800 uppercase tracking-wider mb-1.5">
                                    Scadenza accettazione <span className="font-normal normal-case text-amber-700/70">(opzionale)</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    className="w-full p-2.5 border border-amber-200 rounded-lg bg-white focus:ring-2 focus:ring-amber-300 outline-none"
                                    value={turnoForm.dataScadenzaAccettazione}
                                    onChange={e => setTurnoForm({ ...turnoForm, dataScadenzaAccettazione: e.target.value })}
                                    min={todayMin}
                                />
                                <p className="text-[11px] text-amber-700/80 mt-1.5 leading-snug">
                                    Se compilata, il turno verrà <strong>rifiutato automaticamente</strong> se il collaboratore non accetta entro questa data/ora.
                                </p>
                            </div>

                            <div className="pt-2">
                                <button
                                    onClick={submitTurnoCalendario}
                                    disabled={isAssigning || !turnoForm.servizioId || !turnoForm.collaboratoreId || !turnoForm.oraInizio || !turnoForm.oraFine}
                                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3.5 rounded-xl font-bold shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isAssigning ? <Loader2 className="animate-spin" size={20}/> : <Clock size={20}/>}
                                    {isAssigning ? 'Creazione turno...' : 'Crea Turno e Avvisa'}
                                </button>
                            </div>
                        </>)}
                    </div>
                </Modal>
            )}
            {avvisoCompresenze.modale}

            <ConflittiDisponibilitaModal
                open={conflittiAssegnazione.length > 0}
                onClose={() => { setConflittiAssegnazione([]); setPendingAssignPayload(null); }}
                collaboratoreNome={conflittiSummary.collab}
                conflitti={conflittiAssegnazione}
                creatiCount={conflittiSummary.creati}
                totaleRichiesto={conflittiSummary.totale}
                onConferma={pendingAssignPayload ? handleConfermaOverrideCalendario : undefined}
            />
        </div>
    );
};

export default CalendarioView;